import { WhatsAppProducts } from "@commerce/channels/whatsapp-preview/WhatsAppPreview";
import { Markdown } from "@commerce/components/Markdown";
import {
	calendarDaysApart,
	dayHeading,
	formatDuration,
	sameDay,
	Time,
	useNow,
	useTimeZone,
} from "@commerce/components/time";
import { deliveryOf } from "@repo/api/modules/channels/types";
import { UserAvatar } from "@shared/components/UserAvatar";
import { Badge } from "@ui/components/badge";
import { Bubble, BubbleContent } from "@ui/components/bubble";
import { Button } from "@ui/components/button";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@ui/components/collapsible";
import {
	Empty,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from "@ui/components/empty";
import { InfoTip } from "@ui/components/info-tip";
import { Kbd, KbdGroup } from "@ui/components/kbd";
import { Marker, MarkerContent } from "@ui/components/marker";
import { Message, MessageContent, MessageFooter } from "@ui/components/message";
import {
	MessageScroller,
	MessageScrollerButton,
	MessageScrollerContent,
	MessageScrollerItem,
	MessageScrollerProvider,
	MessageScrollerViewport,
} from "@ui/components/message-scroller";
import { Skeleton } from "@ui/components/skeleton";
import { Spinner } from "@ui/components/spinner";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@ui/components/tooltip";
import { cn } from "@ui/lib";
import { getToolName, isTextUIPart, isToolUIPart, type UIMessage } from "ai";
import {
	ArrowLeftIcon,
	BotIcon,
	CheckIcon,
	ChevronDownIcon,
	MessagesSquareIcon,
	PanelRightIcon,
	RotateCcwIcon,
	UserIcon,
} from "lucide-react";
import { Fragment, type ReactNode } from "react";
import {
	AssigneeMenu,
	HandoffBar,
	StaffComposer,
	TakeOverButton,
} from "./Handoff";
import {
	CHANNEL_LABEL,
	formatCost,
	formatMs,
	formatTokens,
	INPUT_META,
	INPUTS,
	inputsLabel,
	STATUS_META,
} from "./meta";
import { ToolCall } from "./ToolCall";
import type { ConversationDetail, TranscriptMessage } from "./types";
import { CallCard, CallOffset } from "./whatsapp/CallCard";
import { ChannelMessageView } from "./whatsapp/ChannelMessageView";
import { DeliveryTicks } from "./whatsapp/DeliveryTicks";
import {
	type ChannelMessageRow,
	channelRowsOf,
	entryLabel,
} from "./whatsapp/types";
import { WindowChip } from "./whatsapp/WindowChip";

/** A first word slower than this is highlighted on the turn. */
const SLOW_TTFT_MS = 3000;

export function Transcript({
	detail,
	onBack,
	onOpenDetails,
	onSetStatus,
	statusPending,
}: {
	detail: ConversationDetail;
	onBack: () => void;
	/** Opens the details sheet (the button hides where details are docked). */
	onOpenDetails: () => void;
	onSetStatus: (status: "RESOLVED" | "ACTIVE") => void;
	statusPending: boolean;
}) {
	const status = STATUS_META[detail.status];
	const closed =
		detail.status === "RESOLVED" || detail.status === "ABANDONED";
	const escalated = detail.status === "ESCALATED";
	const timeZone = useTimeZone();
	const started = new Date(detail.createdAt);
	const ended = new Date(detail.lastMessageAt);
	const inputs = INPUTS.filter((input) =>
		detail.messages.some((m) => m.input === input),
	);
	const meta = [
		inputsLabel(inputs),
		`${detail.messageCount} messages`,
		detail.language,
		CHANNEL_LABEL[detail.channel],
		detail.store.name,
		entryLabel(detail.entry, () => detail.entryStoreName),
	].filter(Boolean);
	const items = transcriptItems(detail.messages);
	const tapped = tappedReplies(detail.messages);

	return (
		<div className="flex min-h-0 flex-1 flex-col">
			{/* Below lg the actions take a second row, so the title keeps the width. */}
			<header className="flex shrink-0 flex-wrap items-center gap-2 border-border/70 border-b px-3 py-2.5 md:px-5">
				<Button
					variant="ghost"
					size="icon"
					onClick={onBack}
					aria-label="Back to conversations"
					className="-ml-1 lg:hidden"
				>
					<ArrowLeftIcon />
				</Button>
				<div className="min-w-0 flex-1">
					<div className="flex min-w-0 items-center gap-2">
						<h2
							dir="auto"
							className="truncate font-semibold text-sm"
						>
							{detail.title || "Untitled conversation"}
						</h2>
						<Badge
							variant={status.variant}
							className="shrink-0 max-sm:hidden"
						>
							{status.label}
						</Badge>
						{detail.channel === "WHATSAPP" ? (
							<WindowChip
								lastInboundAt={detail.lastInboundAt}
								freeEntryEndsAt={detail.freeEntryEndsAt}
								className="max-sm:hidden"
							/>
						) : null}
					</div>
					<p className="flex min-w-0 flex-wrap items-center gap-x-1.5 text-muted-foreground text-xs">
						<span className="truncate">{meta.join(" · ")}</span>
						{/* On a phone the line wraps here; a lone dot would start the next line. */}
						<span aria-hidden className="max-sm:hidden">
							·
						</span>
						<span className="whitespace-nowrap">
							Started <Time at={started} label="Started" />
						</span>
						{ended.getTime() - started.getTime() >= 60_000 ? (
							<>
								<span aria-hidden>·</span>
								<span className="whitespace-nowrap">
									last message{" "}
									<Time
										at={ended}
										as="relative"
										label="Last message"
									/>
								</span>
								<span aria-hidden className="max-sm:hidden">
									·
								</span>
								<span
									className="whitespace-nowrap max-sm:hidden"
									title="From the first message to the last"
								>
									{formatDuration(
										ended.getTime() - started.getTime(),
									)}{" "}
									long
								</span>
							</>
						) : null}
						<span aria-hidden className="max-sm:hidden">
							·
						</span>
						<span className="max-sm:hidden">
							{formatCost(detail.costUsd)}
						</span>
					</p>
				</div>
				<div className="flex shrink-0 items-center gap-2 max-lg:order-last max-lg:basis-full max-lg:justify-end">
					<AssigneeMenu detail={detail} />
					<TakeOverButton detail={detail} />
					<Button
						size="sm"
						variant={
							escalated && !detail.handoffRequestedAt
								? "default"
								: "outline"
						}
						disabled={statusPending}
						onClick={() =>
							onSetStatus(closed ? "ACTIVE" : "RESOLVED")
						}
					>
						{statusPending ? (
							<Spinner data-icon="inline-start" />
						) : closed ? (
							<RotateCcwIcon data-icon="inline-start" />
						) : (
							<CheckIcon data-icon="inline-start" />
						)}
						{closed
							? "Reopen"
							: escalated
								? "Mark handled"
								: "Resolve"}
					</Button>
				</div>
				<Tooltip>
					<TooltipTrigger
						render={
							<Button
								variant="ghost"
								size="icon"
								onClick={onOpenDetails}
								aria-label="Shopper, basket and details"
								className="2xl:hidden"
							/>
						}
					>
						<PanelRightIcon />
					</TooltipTrigger>
					<TooltipContent>Shopper, basket and details</TooltipContent>
				</Tooltip>
			</header>

			<HandoffBar detail={detail} />

			<WhatsAppProducts value={detail.channelProducts}>
				<MessageScrollerProvider defaultScrollPosition="end" autoScroll>
					<MessageScroller className="flex-1">
						<MessageScrollerViewport aria-label="Conversation transcript">
							<MessageScrollerContent className="mx-auto w-full max-w-3xl gap-6 px-3 py-6 md:px-6">
								{items.map((item, index) => {
									const first = item.messages[0];
									if (!first) {
										return null;
									}
									const at = new Date(first.createdAt);
									const previous =
										items[index - 1]?.messages.at(-1);
									const newDay =
										!previous ||
										!sameDay(
											new Date(previous.createdAt),
											at,
											timeZone,
										);
									return (
										<Fragment key={first.id}>
											{newDay ? (
												<Marker
													variant="separator"
													className="text-xs"
												>
													<MarkerContent>
														<DayLabel at={at} />
													</MarkerContent>
												</Marker>
											) : null}
											<MessageScrollerItem
												messageId={first.id}
											>
												{item.callId ? (
													<CallCard
														organizationId={
															detail.organizationId
														}
														callId={item.callId}
														turns={item.messages.map(
															(m) => ({
																id: m.id,
																callOffsetMs:
																	callOf(m)
																		?.callOffsetMs ??
																	null,
															}),
														)}
													>
														{(seek) =>
															item.messages.map(
																(m) => (
																	<TranscriptEntry
																		key={
																			m.id
																		}
																		message={
																			m
																		}
																		at={
																			new Date(
																				m.createdAt,
																			)
																		}
																		tapped={
																			tapped
																		}
																		offset={
																			<CallOffset
																				offsetMs={
																					callOf(
																						m,
																					)
																						?.callOffsetMs ??
																					null
																				}
																				onSeek={
																					seek
																				}
																			/>
																		}
																	/>
																),
															)
														}
													</CallCard>
												) : (
													<TranscriptEntry
														message={first}
														at={at}
														tapped={tapped}
													/>
												)}
											</MessageScrollerItem>
										</Fragment>
									);
								})}
							</MessageScrollerContent>
						</MessageScrollerViewport>
						<MessageScrollerButton />
					</MessageScroller>
				</MessageScrollerProvider>
			</WhatsAppProducts>
			<StaffComposer detail={detail} />
		</div>
	);
}

/** Mic or phone on a turn that was spoken; typing needs no badge. */
function InputMark({ input }: { input: TranscriptMessage["input"] }) {
	if (input === "TEXT") {
		return null;
	}
	const meta = INPUT_META[input];
	return (
		<Badge variant="outline" className="gap-1">
			<meta.icon aria-hidden />
			{meta.label}
		</Badge>
	);
}

/** "Today", "Yesterday", then the date, all in the panel's timezone. */
function DayLabel({ at }: { at: Date }) {
	const timeZone = useTimeZone();
	const now = new Date(useNow() || at.getTime());
	const days = calendarDaysApart(at, now, timeZone);
	if (days === 0) {
		return "Today";
	}
	if (days === 1) {
		return "Yesterday";
	}
	return dayHeading(at, timeZone);
}

function TranscriptEntry({
	message,
	at,
	tapped,
	offset,
}: {
	message: TranscriptMessage;
	at: Date;
	/** The button or row id the shopper tapped, by the wamid of the message it answered. */
	tapped: ReadonlyMap<string, string>;
	/** A call turn's place in the recording. */
	offset?: ReactNode;
}) {
	const parts = (message.parts ?? []) as UIMessage["parts"];
	const rows = channelRowsOf(message);
	const text = parts
		.filter(isTextUIPart)
		.map((p) => p.text)
		.join("");

	if (message.role === "user") {
		const inbound = rows.filter((row) => row.direction === "IN");
		return (
			<Message align="end">
				<MessageContent className="items-end">
					{inbound.length ? (
						inbound.map((row) => (
							<ChannelMessageView
								key={row.id}
								row={row}
								readAs={
									READ_AS_KINDS.has(row.kind) ? text : null
								}
							/>
						))
					) : (
						<Bubble
							variant="secondary"
							align="end"
							className="max-w-[85%] sm:max-w-[75%]"
						>
							<BubbleContent
								dir="auto"
								className="whitespace-pre-wrap"
							>
								{text}
							</BubbleContent>
						</Bubble>
					)}
					<MessageFooter>
						<span className="flex items-center gap-1.5 font-normal tabular">
							{offset}
							<InputMark input={message.input} />
							Shopper · <Time at={at} />
						</span>
					</MessageFooter>
				</MessageContent>
			</Message>
		);
	}

	const outbound = rows.filter((row) => row.direction === "OUT");

	if (message.role === "staff") {
		const author = message.author;
		return (
			<Message align="start" className="gap-3">
				{author ? (
					<UserAvatar
						size="sm"
						name={author.name}
						avatarUrl={author.image}
						className="mt-0.5 size-7"
					/>
				) : (
					<Avatar speaker="user" />
				)}
				<MessageContent>
					{outbound.length ? (
						<SentRows rows={outbound} tapped={tapped} />
					) : (
						<Bubble
							variant="tinted"
							className="max-w-[85%] sm:max-w-[75%]"
						>
							<BubbleContent
								dir="auto"
								className="whitespace-pre-wrap"
							>
								{text}
							</BubbleContent>
						</Bubble>
					)}
					<MessageFooter className="flex-wrap gap-x-2 gap-y-1">
						<span className="font-normal tabular">
							{author ? author.name : "Your team"} · Team ·{" "}
							<Time at={at} />
						</span>
						<DeliveryNote parts={parts} />
					</MessageFooter>
				</MessageContent>
			</Message>
		);
	}

	if (outbound.length) {
		return (
			<Message align="start" className="gap-3">
				<Avatar speaker="assistant" />
				<MessageContent className="min-w-0 gap-2">
					<SentRows rows={outbound} tapped={tapped} ticks={false} />
					<Collapsible>
						<SentSummary
							rows={outbound}
							message={message}
							offset={offset}
						/>
						<CollapsibleContent className="mt-2 flex flex-col gap-2 border-border/70 border-l pl-3">
							<AssistantParts id={message.id} parts={parts} />
							<TurnMetrics message={message} at={at} />
						</CollapsibleContent>
					</Collapsible>
					<DeliveryNote parts={parts} />
				</MessageContent>
			</Message>
		);
	}

	return (
		<Message align="start" className="gap-3">
			<Avatar speaker="assistant" />
			<MessageContent className="gap-2">
				<AssistantParts id={message.id} parts={parts} />
				<TurnMetrics message={message} at={at} offset={offset} />
				<DeliveryNote parts={parts} />
			</MessageContent>
		</Message>
	);
}

/** Inbound kinds whose turn text is how the agent read them: a photo's reading, a note's words. */
const READ_AS_KINDS = new Set(["image", "voice", "audio"]);

/** What WhatsApp showed for one reply, each message drawn from its exact payload. */
function SentRows({
	rows,
	tapped,
	ticks = true,
}: {
	rows: readonly ChannelMessageRow[];
	tapped: ReadonlyMap<string, string>;
	ticks?: boolean;
}) {
	return (
		<div className="flex min-w-0 flex-col gap-1.5">
			{rows.map((row) => (
				<ChannelMessageView
					key={row.id}
					row={row}
					tappedId={row.wamid ? tapped.get(row.wamid) : null}
					showTicks={ticks}
				/>
			))}
		</div>
	);
}

/**
 * "2 messages · ✓✓ · $0.0157 Meta · 2.1 s", then the agent's own steps one click away. The
 * ticks are the furthest the last message got; Meta's cost is the sum of what each was priced.
 */
function SentSummary({
	rows,
	message,
	offset,
}: {
	rows: readonly ChannelMessageRow[];
	message: TranscriptMessage;
	offset?: ReactNode;
}) {
	const last = rows.at(-1);
	const metaCost = rows.reduce(
		(sum, row) => sum + Number(row.costUsd ?? 0),
		0,
	);
	return (
		<p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-muted-foreground text-xs tabular">
			{offset}
			<InputMark input={message.input} />
			<span>
				Sent on WhatsApp · <Time at={new Date(message.createdAt)} />
			</span>
			<span aria-hidden>·</span>
			<span>
				{rows.length} {rows.length === 1 ? "message" : "messages"}
			</span>
			{last ? <DeliveryTicks row={last} /> : null}
			{metaCost > 0 ? (
				<>
					<span aria-hidden>·</span>
					<span title="What Meta charges for these messages, estimated">
						{formatCost(metaCost)} Meta
					</span>
				</>
			) : null}
			{message.latencyMs != null ? (
				<>
					<span aria-hidden>·</span>
					<span title="Whole reply, including searches">
						{formatMs(message.latencyMs)}
					</span>
				</>
			) : null}
			<CollapsibleTrigger
				render={
					<Button
						variant="ghost"
						size="xs"
						className="text-muted-foreground data-panel-open:[&>svg]:rotate-180"
					/>
				}
			>
				Agent steps
				<ChevronDownIcon data-icon="inline-end" />
			</CollapsibleTrigger>
		</p>
	);
}

/** One entry per message, except the turns of one call, which share a card. */
interface TranscriptItem {
	callId: string | null;
	messages: TranscriptMessage[];
}

export function transcriptItems(
	messages: readonly TranscriptMessage[],
): TranscriptItem[] {
	const items: TranscriptItem[] = [];
	for (const message of messages) {
		const callId = callOf(message)?.callId ?? null;
		const last = items.at(-1);
		if (callId && last?.callId === callId) {
			last.messages.push(message);
		} else {
			items.push({ callId, messages: [message] });
		}
	}
	return items;
}

/** The call a turn was said on, once `conversations.get` carries `callId` (spec 2.4). */
function callOf(
	message: object,
): { callId: string; callOffsetMs: number | null } | null {
	const value = message as { callId?: unknown; callOffsetMs?: unknown };
	return typeof value.callId === "string"
		? {
				callId: value.callId,
				callOffsetMs:
					typeof value.callOffsetMs === "number"
						? value.callOffsetMs
						: null,
			}
		: null;
}

/** Which button or list row the shopper tapped, by the wamid of the message it answered. */
export function tappedReplies(
	messages: readonly TranscriptMessage[],
): Map<string, string> {
	const tapped = new Map<string, string>();
	for (const message of messages) {
		for (const row of channelRowsOf(message)) {
			const content = (
				row.payload as { content?: { type?: unknown; id?: unknown } }
			)?.content;
			if (
				row.direction === "IN" &&
				row.replyToWamid &&
				content?.type === "reply" &&
				typeof content.id === "string"
			) {
				tapped.set(row.replyToWamid, content.id);
			}
		}
	}
	return tapped;
}

/** A saved message the shopper may not have received (`deliveryOf`). */
function DeliveryNote({ parts }: { parts: unknown }) {
	const delivery = deliveryOf(parts);
	if (!delivery) {
		return null;
	}
	return (
		<Badge variant="warning" className="w-fit">
			{delivery === "not_sent"
				? "Not sent: a person took over while the agent was writing"
				: "Delivery not confirmed"}
		</Badge>
	);
}

/**
 * A key per part that holds while the message is still streaming in: a tool call is
 * keyed by its own id, any other part by the tool call before it and its place after it.
 */
function keyedParts(messageId: string, parts: UIMessage["parts"]) {
	let anchor = `${messageId}-start`;
	let since = 0;
	return parts.map((part) => {
		if (isToolUIPart(part)) {
			anchor = part.toolCallId;
			since = 0;
			return { key: anchor, part };
		}
		since += 1;
		return { key: `${anchor}-${since}`, part };
	});
}

/** Text and tool calls of one agent message. `streaming` marks unfinished tool calls as running. */
export function AssistantParts({
	id,
	parts,
	streaming = false,
}: {
	id: string;
	parts: UIMessage["parts"];
	streaming?: boolean;
}) {
	return (
		<>
			{keyedParts(id, parts).map(({ key, part }) => {
				if (isTextUIPart(part)) {
					return part.text.trim() ? (
						<Markdown
							key={key}
							streaming={streaming && part.state === "streaming"}
							className="text-pretty text-foreground text-sm leading-relaxed"
						>
							{part.text}
						</Markdown>
					) : null;
				}
				if (isToolUIPart(part)) {
					return (
						<ToolCall
							key={key}
							streaming={streaming}
							part={{
								toolName: getToolName(part),
								state: part.state,
								input: part.input,
								output:
									part.state === "output-available"
										? part.output
										: undefined,
								errorText:
									part.state === "output-error"
										? part.errorText
										: undefined,
							}}
						/>
					);
				}
				return null;
			})}
		</>
	);
}

/** One line under an agent turn: when, how fast, what it cost. Model and tokens on hover. */
export function TurnMetrics({
	message: m,
	at,
	offset,
}: {
	message: TranscriptMessage;
	at: Date;
	/** A call turn's place in the recording. */
	offset?: ReactNode;
}) {
	const slow = m.ttftMs != null && m.ttftMs > SLOW_TTFT_MS;
	const technical = [
		m.model ? `Model: ${m.model}` : null,
		m.inputTokens != null
			? `Tokens: ${formatTokens(m.inputTokens)} in, ${formatTokens(m.outputTokens)} out`
			: null,
	].filter(Boolean);

	return (
		<p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-muted-foreground text-xs tabular">
			{offset}
			<InputMark input={m.input} />
			<span>
				Agent · <Time at={at} />
			</span>
			{m.ttftMs != null ? (
				<>
					<span aria-hidden>·</span>
					<span
						title="How long the shopper waited before the reply started"
						className={cn(
							slow &&
								"font-medium text-warning-foreground dark:text-warning",
						)}
					>
						first word {formatMs(m.ttftMs)}
					</span>
				</>
			) : null}
			{m.latencyMs != null ? (
				<>
					<span aria-hidden>·</span>
					<span title="Whole reply, including searches">
						done in {formatMs(m.latencyMs)}
					</span>
				</>
			) : null}
			{m.costUsd != null ? (
				<>
					<span aria-hidden>·</span>
					<span>{formatCost(m.costUsd)}</span>
				</>
			) : null}
			{technical.length ? (
				<InfoTip label="Model and tokens">
					<span className="flex flex-col gap-0.5">
						{technical.map((line) => (
							<span key={line}>{line}</span>
						))}
					</span>
				</InfoTip>
			) : null}
		</p>
	);
}

export function Avatar({ speaker }: { speaker: "user" | "assistant" }) {
	const Icon = speaker === "user" ? UserIcon : BotIcon;
	return (
		<span
			aria-hidden
			className={cn(
				"mt-0.5 grid size-7 shrink-0 place-items-center",
				speaker === "user"
					? "rounded-full bg-muted text-muted-foreground"
					: "rounded-lg bg-primary text-primary-foreground",
			)}
		>
			<Icon className="size-3.5" />
		</span>
	);
}

export function TranscriptEmpty() {
	return (
		<Empty className="m-auto">
			<EmptyHeader>
				<EmptyMedia variant="icon">
					<MessagesSquareIcon />
				</EmptyMedia>
				<EmptyTitle>Pick a conversation</EmptyTitle>
			</EmptyHeader>
			<p className="flex items-center gap-1.5 text-muted-foreground text-xs max-lg:hidden">
				<KbdGroup>
					<Kbd>↑</Kbd>
					<Kbd>↓</Kbd>
				</KbdGroup>
				to browse
			</p>
		</Empty>
	);
}

export function TranscriptSkeleton() {
	return (
		<div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-6 py-6">
			{Array.from({ length: 4 }).map((_, i) => (
				<Skeleton
					key={i}
					className={cn("h-14", i % 2 ? "w-2/3 self-end" : "w-full")}
				/>
			))}
		</div>
	);
}
